import { createHttpClient } from '../../core/api/http.js';
import { formatCurrencyGBP, formatNumber } from '../../core/utils/format.js';
import { getQueryParam } from '../../core/utils/params.js';

function resolveConfig(root) {
    const baseUrl = root?.dataset?.apiBase || root?.dataset?.baseUrl || 'https://api.dwellow.co.uk';
    const slug = getQueryParam('quote') || root?.dataset?.quote || null;
    return { baseUrl, slug };
}

function buildErrorMessage(err, fallback) {
    if (!err) return fallback;
    return err.message || fallback;
}

function createBoilerApi({ baseUrl } = {}) {
    const http = createHttpClient({ baseUrl });
    return {
        getQuote: slug => http.get('/api/boiler-heatable/get-quote', { params: { slug } }),
        getRecommended: slug => http.get('/api/boiler-heatable/get-quote-recommended', { params: { slug } })
    };
}

/** Normalize any Wized response shape to an array */
function asArray(input) {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (Array.isArray(input.data)) return input.data;
    if (input?.data?.results && Array.isArray(input.data.results)) return input.data.results;
    if (Array.isArray(input.results)) return input.results;
    return [];
}

function buildRecommended(allResults, recommendedResults) {
    const recById = {};
    for (const row of recommendedResults) {
        const key = String(row?.package_id ?? '');
        if (key) recById[key] = row;
    }

    const enrichedAll = allResults.map(p => {
        const key = String(p?.id ?? '');
        const rec = recById[key] || null;
        return { ...p, rec };
    });

    const byId = Object.fromEntries(enrichedAll.map(p => [String(p?.id ?? ''), p]));
    const recommendedInApiOrder = recommendedResults
        .map(row => byId[String(row?.package_id ?? '')])
        .filter(Boolean)
        .map(p => ({ ...p, rec: recById[String(p.id)] }));

    const labelOrder = { Best: 1, Popular: 2, Better: 3, Good: 4, Premium: 5, Basic: 6 };
    const recommended = recommendedInApiOrder.sort((a, b) => {
        const A = labelOrder[a.rec?.label] ?? 99;
        const B = labelOrder[b.rec?.label] ?? 99;
        if (A !== B) return A - B;
        const pa = Number(a.fixed_price);
        const pb = Number(b.fixed_price);
        const safeA = Number.isFinite(pa) ? pa : Number.POSITIVE_INFINITY;
        const safeB = Number.isFinite(pb) ? pb : Number.POSITIVE_INFINITY;
        return safeA - safeB;
    });

    return { enrichedAll, recommended };
}

function pickPackageImage(pkg) {
    const primary = Array.isArray(pkg?.images) && pkg.images.length ? pkg.images[0] : null;
    const fallback = Array.isArray(pkg?.item?.images) && pkg.item.images.length ? pkg.item.images[0] : null;
    const image = primary || fallback;
    return image?.document_url || image?.document_thumbnail_url || null;
}

function pickBrandLogo(pkg) {
    return pkg?.item?.brand?.logo_url || pkg?.item?.brand?.logo_thumbnail_url || null;
}

function getPackageName(pkg) {
    return pkg?.name || pkg?.item?.name || pkg?.item?.description || 'Package';
}

function getPackageDescription(pkg) {
    return pkg?.description || pkg?.item?.description || null;
}

function getWarrantyText(pkg) {
    const years = pkg?.item?.warranty;
    if (years === undefined || years === null) return null;
    const unit = pkg?.item?.warrantyType?.description || 'Years';
    return `${years} ${unit}`;
}

function getSizeText(pkg) {
    const size = pkg?.size ?? pkg?.item?.sizes?.[0]?.size ?? null;
    if (size === undefined || size === null) return null;
    const metric = pkg?.item?.sizes?.[0]?.metric?.description || 'kW';
    return `${size} ${metric}`;
}

function toNumberOrNaN(value) {
    if (value === '' || value === null || value === undefined) return Number.NaN;
    const num = Number(value);
    return Number.isFinite(num) ? num : Number.NaN;
}

export function registerBoilerResultsJourney() {
    if (typeof document === 'undefined') return;

    const createStore = () => ({
        loading: true,
        error: null,
        slug: null,
        api: null,
        heatableResults: null,
        heatableRecommendedRaw: null,
        heatableAll: [],
        heatableRecommended: [],
        boilerSortOption: 'price-asc',
        searchTerm: '',
        filterRecommendedOnly: false,
        filterBudgetOnly: false,
        brandFilter: '',
        minWarranty: null,
        sizeMin: null,
        sizeMax: null,
        priceMin: null,
        priceMax: null,

        init() {
            const { baseUrl, slug } = resolveConfig(this.$root);
            this.slug = slug;
            this.api = createBoilerApi({ baseUrl });

            if (!this.slug) {
                this.loading = false;
                return;
            }

            this.loadResults();
        },

        async loadResults() {
            this.loading = true;
            this.error = null;
            try {
                const [results, recommended] = await Promise.all([
                    this.api.getQuote(this.slug),
                    this.api.getRecommended(this.slug)
                ]);
                this.heatableResults = results;
                this.heatableRecommendedRaw = recommended;

                const all = asArray(results);
                const recs = asArray(recommended);
                const { enrichedAll, recommended: normalizedRecs } = buildRecommended(all, recs);

                this.heatableAll = enrichedAll;
                this.heatableRecommended = normalizedRecs;
                this.boilerSortOption = 'price-asc';
                this.applySort();
            } catch (err) {
                this.error = buildErrorMessage(err, 'Unable to load boiler results.');
            } finally {
                this.loading = false;
            }
        },

        setSortOption(option) {
            this.boilerSortOption = option;
            this.applySort();
        },

        formatCurrency(value) {
            return formatCurrencyGBP(value);
        },

        formatNumber(value) {
            return formatNumber(value);
        },

        packageName(pkg) {
            return getPackageName(pkg);
        },

        packageDescription(pkg) {
            return getPackageDescription(pkg);
        },

        packageImage(pkg) {
            return pickPackageImage(pkg);
        },

        brandName(pkg) {
            return pkg?.item?.brand?.description || null;
        },

        brandLogo(pkg) {
            return pickBrandLogo(pkg);
        },

        warrantyText(pkg) {
            return getWarrantyText(pkg);
        },

        sizeText(pkg) {
            return getSizeText(pkg);
        },

        financeMessage(pkg) {
            const paymentOptions = Array.isArray(pkg?.paymentOptions) ? pkg.paymentOptions : [];
            const financePO = paymentOptions.find(po => po?.method?.description === 'Finance');
            if (!financePO) return '';

            const allOptions = (financePO.providers || [])
                .flatMap(provider => provider?.options || [])
                .map(opt => ({
                    apr: Number(opt?.providerRate?.rate?.apr),
                    termMonths: Number(opt?.providerRate?.rate?.term)
                }))
                .filter(opt => Number.isFinite(opt.apr) && Number.isFinite(opt.termMonths));

            if (allOptions.length === 0) return '';

            const hasZeroApr = allOptions.some(opt => opt.apr === 0);
            const nonZeroAprTerms = allOptions.filter(opt => opt.apr > 0).map(opt => opt.termMonths);
            const maxTermYears =
                nonZeroAprTerms.length > 0 ? Math.ceil(Math.max(...nonZeroAprTerms) / 12) : 0;

            if (hasZeroApr && nonZeroAprTerms.length === 0) {
                return 'Flexible finance options including 0% APR';
            }
            if (hasZeroApr && nonZeroAprTerms.length > 0) {
                return `Flexible finance options including 0% APR and up to ${maxTermYears} years with interest`;
            }
            if (!hasZeroApr && nonZeroAprTerms.length > 0) {
                return `Flexible finance options including terms up to ${maxTermYears} years with interest`;
            }
            return '';
        },

        postcodeDistrict() {
            const answers = Array.isArray(this.heatableResults?.answers) ? this.heatableResults.answers : [];
            const row = answers.find(a => String(a?.attribute || '').toLowerCase() === 'postcode');
            const value = String(row?.answer || '').trim();
            if (!value) return '';
            const trimmed = value.replace(/\s+/g, '');
            if (trimmed.length <= 3) return value;
            return trimmed.slice(0, -3);
        },

        answerValue(attribute, field = 'answer') {
            const answers = Array.isArray(this.heatableResults?.answers) ? this.heatableResults.answers : [];
            const key = String(attribute || '').toLowerCase();
            const row = answers.find(a => String(a?.attribute || '').toLowerCase() === key);
            const value = row?.[field];
            return value == null ? '' : String(value);
        },

        bedroomsDescription() {
            return this.answerValue('Bedrooms', 'answer_description');
        },

        propertyAnswer() {
            return this.answerValue('Property', 'answer');
        },

        get filteredResults() {
            const term = (this.searchTerm || '').toLowerCase().trim();
            const brandTerm = (this.brandFilter || '').toLowerCase().trim();
            const minWarranty = toNumberOrNaN(this.minWarranty);
            const sizeMin = toNumberOrNaN(this.sizeMin);
            const sizeMax = toNumberOrNaN(this.sizeMax);
            const priceMin = toNumberOrNaN(this.priceMin);
            const priceMax = toNumberOrNaN(this.priceMax);

            return (this.heatableAll || []).filter(pkg => {
                if (this.filterRecommendedOnly && !pkg.rec) return false;
                if (this.filterBudgetOnly && !pkg.item?.meta?.is_budget) return false;

                if (Number.isFinite(minWarranty) && (pkg.item?.warranty ?? 0) < minWarranty) return false;

                const sizeValue = Number(pkg.size ?? pkg.item?.sizes?.[0]?.size);
                if (Number.isFinite(sizeMin) && (!Number.isFinite(sizeValue) || sizeValue < sizeMin)) return false;
                if (Number.isFinite(sizeMax) && (!Number.isFinite(sizeValue) || sizeValue > sizeMax)) return false;

                const priceValue = Number(pkg.fixed_price ?? pkg.total_fixed_price);
                if (Number.isFinite(priceMin) && (!Number.isFinite(priceValue) || priceValue < priceMin)) return false;
                if (Number.isFinite(priceMax) && (!Number.isFinite(priceValue) || priceValue > priceMax)) return false;

                if (brandTerm) {
                    const brandName = (pkg.item?.brand?.description || '').toLowerCase();
                    if (!brandName.includes(brandTerm)) return false;
                }

                if (term) {
                    const name = (pkg.name || pkg.item?.name || '').toLowerCase();
                    const brandName = (pkg.item?.brand?.description || '').toLowerCase();
                    if (!name.includes(term) && !brandName.includes(term)) return false;
                }

                return true;
            });
        },

        get availableBrands() {
            const set = new Set();
            for (const pkg of this.heatableAll || []) {
                const brand = pkg.item?.brand?.description;
                if (brand) set.add(brand);
            }
            return Array.from(set).sort((a, b) => a.localeCompare(b));
        },

        get appliedFilters() {
            const tokens = [];
            if (this.filterRecommendedOnly) tokens.push({ type: 'recommended', label: 'Recommended' });
            if (this.filterBudgetOnly) tokens.push({ type: 'budget', label: 'Budget' });
            if (this.brandFilter) tokens.push({ type: 'brand', label: this.brandFilter });
            if (toNumberOrNaN(this.minWarranty) === toNumberOrNaN(this.minWarranty)) {
                tokens.push({ type: 'warranty', label: `${this.minWarranty}+ years` });
            }
            if (toNumberOrNaN(this.sizeMin) === toNumberOrNaN(this.sizeMin)) {
                tokens.push({ type: 'sizeMin', label: `Size ≥ ${this.sizeMin}kW` });
            }
            if (toNumberOrNaN(this.sizeMax) === toNumberOrNaN(this.sizeMax)) {
                tokens.push({ type: 'sizeMax', label: `Size ≤ ${this.sizeMax}kW` });
            }
            if (toNumberOrNaN(this.priceMin) === toNumberOrNaN(this.priceMin)) {
                tokens.push({ type: 'priceMin', label: `Price ≥ ${this.priceMin}` });
            }
            if (toNumberOrNaN(this.priceMax) === toNumberOrNaN(this.priceMax)) {
                tokens.push({ type: 'priceMax', label: `Price ≤ ${this.priceMax}` });
            }
            if (this.searchTerm) tokens.push({ type: 'search', label: `Search: ${this.searchTerm}` });
            return tokens;
        },

        removeFilter(token) {
            if (!token) return;
            if (token.type === 'recommended') this.filterRecommendedOnly = false;
            if (token.type === 'budget') this.filterBudgetOnly = false;
            if (token.type === 'brand') this.brandFilter = '';
            if (token.type === 'warranty') this.minWarranty = null;
            if (token.type === 'sizeMin') this.sizeMin = null;
            if (token.type === 'sizeMax') this.sizeMax = null;
            if (token.type === 'priceMin') this.priceMin = null;
            if (token.type === 'priceMax') this.priceMax = null;
            if (token.type === 'search') this.searchTerm = '';
        },

        clearFilters() {
            this.searchTerm = '';
            this.filterRecommendedOnly = false;
            this.filterBudgetOnly = false;
            this.brandFilter = '';
            this.minWarranty = null;
            this.sizeMin = null;
            this.sizeMax = null;
            this.priceMin = null;
            this.priceMax = null;
        },

        applySort() {
            if (!this.heatableAll || this.heatableAll.length === 0) return;
            switch (this.boilerSortOption) {
                case 'price-asc':
                    this.heatableAll.sort((a, b) => Number(a.fixed_price) - Number(b.fixed_price));
                    break;
                case 'price-desc':
                    this.heatableAll.sort((a, b) => Number(b.fixed_price) - Number(a.fixed_price));
                    break;
                case 'size-asc':
                    this.heatableAll.sort((a, b) => Number(a.size) - Number(b.size));
                    break;
                case 'size-desc':
                    this.heatableAll.sort((a, b) => Number(b.size) - Number(a.size));
                    break;
                case 'warranty-desc':
                    this.heatableAll.sort((a, b) => Number(b.item?.warranty) - Number(a.item?.warranty));
                    break;
                default:
                    break;
            }
        }
    });

    if (typeof window !== 'undefined') {
        window.boilerResults = createStore;
    }

    const refreshAlpine = () => {
        if (!window.Alpine || typeof window.Alpine.initTree !== 'function') return;
        const root = document.querySelector('[data-journey="boiler-results"]');
        if (root) window.Alpine.initTree(root);
    };
    const ensureMounted = () => {
        if (!window.Alpine || typeof window.Alpine.initTree !== 'function') return;
        const root = document.querySelector('[data-journey="boiler-results"]');
        if (root) {
            window.Alpine.initTree(root);
            return;
        }
        const observer = new MutationObserver(() => {
            const nextRoot = document.querySelector('[data-journey="boiler-results"]');
            if (!nextRoot) return;
            window.Alpine.initTree(nextRoot);
            observer.disconnect();
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 5000);
    };

    const register = () => {
        if (!window.Alpine || typeof window.Alpine.data !== 'function') return;
        window.Alpine.data('boilerResults', createStore);
        refreshAlpine();
    };

    if (window.Alpine) {
        register();
    } else {
        document.addEventListener('alpine:init', register);
    }

    document.addEventListener('DOMContentLoaded', refreshAlpine);
    document.addEventListener('DOMContentLoaded', ensureMounted);
}

registerBoilerResultsJourney();
